class SplideSlider extends HTMLElement {
  constructor() {
    super();

    this.onTrackpadWheel = this.onTrackpadWheel.bind(this);
    this.settleTrackpad = this.settleTrackpad.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.root = this.querySelector('.splide');
    this.configElement = this.querySelector('[data-splide-config]');

    if (!this.root || typeof window.Splide !== 'function') return;

    this.initSlider();

    if (this.trackpadOptions) {
      this.root.addEventListener('wheel', this.onTrackpadWheel, { passive: false });
    }

    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.root?.removeEventListener('wheel', this.onTrackpadWheel);
    window.removeEventListener('resize', this.onResize);
    window.clearTimeout(this.trackpadTimeout);
    window.cancelAnimationFrame(this.trackpadFrame);
    this.destroySlider();
  }

  initSlider() {
    const config = this.getConfig();

    if (!config) return;

    const { trackpad, ...options } = config;

    this.trackpadOptions = trackpad === true ? {} : trackpad;

    const wantsLoop = options.type === 'loop';
    options.type = 'slide'; // Temporarily slide to check for overflow

    this.splide = new window.Splide(this.root, options);
    this.splide.mount();

    this.isCurrentlyLoop = false;

    if (wantsLoop && this.splide.Components.Layout.isOverflow()) {
      this.splide.destroy(true);
      options.type = 'loop';
      this.splide = new window.Splide(this.root, options);
      this.splide.mount();
      this.isCurrentlyLoop = true;
    }

    this.dispatchEvent(
      new CustomEvent('splide-slider:mounted', {
        bubbles: true,
        detail: { splide: this.splide },
      })
    );
  }

  destroySlider() {
    this.splide?.destroy(true);
    this.splide = null;
  }

  onResize() {
    if (!this.splide) return;

    const config = this.getConfig();
    if (!config || config.type !== 'loop') return;

    let hasOverflow = false;

    if (this.isCurrentlyLoop) {
      const trackWidth = this.root.querySelector('.splide__track').clientWidth;
      const slides = this.root.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
      let totalSlidesWidth = 0;

      slides.forEach((slide) => {
        totalSlidesWidth += slide.offsetWidth;
      });

      const opt = this.splide.options;
      const gap = parseFloat(opt.gap) || 0;
      totalSlidesWidth += (slides.length - 1) * gap;

      let paddingLeft = 0;
      let paddingRight = 0;
      if (opt.padding) {
        paddingLeft = parseFloat(opt.padding.left) || parseFloat(opt.padding) || 0;
        paddingRight = parseFloat(opt.padding.right) || parseFloat(opt.padding) || 0;
      }
      totalSlidesWidth += paddingLeft + paddingRight;

      hasOverflow = totalSlidesWidth > trackWidth;
    } else {
      hasOverflow = this.splide.Components.Layout.isOverflow();
    }

    if (hasOverflow !== this.isCurrentlyLoop) {
      this.destroySlider();
      this.initSlider();
    }
  }

  getConfig() {
    if (!this.configElement) return {};

    try {
      const config = JSON.parse(this.configElement.textContent.trim() || '{}');

      if (!config || Array.isArray(config) || typeof config !== 'object') {
        throw new Error('The configuration must be a JSON object.');
      }

      return config;
    } catch (error) {
      console.error('[splide-slider] Invalid configuration.', error, this);
      return null;
    }
  }

  onTrackpadWheel(event) {
    const deltaX = event.deltaX;

    if (
      event.ctrlKey ||
      !this.root.classList.contains('is-overflow') ||
      Math.abs(deltaX) <= Math.abs(event.deltaY) ||
      Math.abs(deltaX) < 1
    ) {
      return;
    }

    const { Move, Scroll } = this.splide.Components;
    const position = this.trackpadPosition ?? Move.getPosition();
    const firstLimit = Move.getLimit(false);
    const lastLimit = Move.getLimit(true);
    const min = Math.min(firstLimit, lastLimit);
    const max = Math.max(firstLimit, lastLimit);
    const destination = Math.max(min, Math.min(max, position - deltaX));

    if (Math.abs(destination - position) < 1) return;

    event.preventDefault();

    if (this.trackpadPosition === null || this.trackpadPosition === undefined) {
      Scroll.cancel();
      Move.cancel();
    }

    this.trackpadPosition = destination;

    if (!this.trackpadFrame) {
      this.trackpadFrame = window.requestAnimationFrame(() => {
        this.trackpadFrame = null;

        if (!this.splide || this.trackpadPosition === null) return;

        Move.translate(this.trackpadPosition);
      });
    }

    window.clearTimeout(this.trackpadTimeout);
    this.trackpadTimeout = window.setTimeout(
      this.settleTrackpad,
      this.trackpadOptions.snapDelay || 120
    );
  }

  settleTrackpad() {
    if (!this.splide) return;

    const { Controller, Move } = this.splide.Components;
    const destination = this.trackpadPosition;

    window.cancelAnimationFrame(this.trackpadFrame);
    this.trackpadFrame = null;

    if (destination === null || destination === undefined) return;

    this.trackpadPosition = null;
    Move.translate(destination);
    Controller.scroll(destination, undefined, this.trackpadOptions.snap !== false);
  }
}

if (!customElements.get('splide-slider')) {
  customElements.define('splide-slider', SplideSlider);
}
